<script setup lang="ts">
import type { APISearchLocationItem, YscDateRange } from '../../types'
import { useModalSteps } from '../../composables/useModalSteps'
import { ref, useNuxtApp, watch } from '#imports'

const { $i18n: { locale } } = useNuxtApp()

interface Props {
  results?: APISearchLocationItem[]
  minDate?: string
  maxDate?: string
  defaultStep?: number
}

const props = withDefaults(defineProps<Props>(), {
  results: () => [],
  minDate: undefined,
  maxDate: undefined,
  defaultStep: 0,
})

const destinationsInputComponentEl = ref<HTMLHtmlElement>()

const onDestinationModalMounted = () => {
  if (destinationsInputComponentEl.value) {
    // @ts-expect-error wrong type, should be type instance of YscSearchDestinationInput
    destinationsInputComponentEl.value.destinationsInputEl?.focus()
  }
}

const emits = defineEmits<{
  'close': []
  'destination-query-change': [string | null]
  'destination-change': [APISearchLocationItem]
  'dates-change': [YscDateRange]
  'submit': []
}>()

const { currentStep, isLastStep, prevStep, nextStep } = useModalSteps({ numberOfSteps: 2, initialStep: props.defaultStep })

const localDestination = ref<string>('')

const skipDestination = () => {
  localDestination.value = ''
  nextStep()
}

const onDestinationQueryChange = (destination: APISearchLocationItem) => {
  emits('destination-change', destination)
  nextStep()
}

const rangeCalendarRef = ref<any>(null)
const onClearRange = () => {
  if (rangeCalendarRef.value) {
    rangeCalendarRef.value.clearRange()
  }
  return {
    start: undefined,
    end: undefined,
  }
}

const localDateRange = ref<YscDateRange>({
  start: undefined,
  end: undefined,
})

watch(localDateRange,
  (val) => {
    emits('dates-change', val)
  },
)

const onSubmit = () => {
  emits('submit')
  emits('close')
}
</script>

<template>
  <YscModal
    fullscreen
    no-padding
    @mounted="onDestinationModalMounted"
    @close="emits('close')"
  >
    <template #header>
      <header class="sticky top-0 z-10 flex justify-between bg-white px-4 sm:px-10 py-4">
        <button
          :class="{ 'text-transparent': currentStep === 0 }"
          class="shrink-0 py-0.5 pr-0.5 md:pl-0.5 md:pr-0"
          @click="prevStep()"
        >
          <YscIconsArrowLeft class="h-6 w-6" />
        </button>

        <button
          class="shrink-0 py-0.5 pr-0.5 md:pl-0.5 md:pr-0"
          @click="emits('close')"
        >
          <YscIconsDelete class="h-6 w-6" />
        </button>
      </header>
    </template>
    <form
      class="relative grow"
    >
      <div v-if="currentStep === 0">
        <YscSearchDestinationInput
          ref="destinationsInputComponentEl"
          v-model="localDestination"
          @update:model-value="emits('destination-query-change', $event)"
        />
        <YscSearchDestinationResults
          :local-destination="localDestination"
          :results="results"
          @destination-click="onDestinationQueryChange"
        />
      </div>
      <div v-else-if="currentStep === 1">
        <div class="w-full border-b border-gray-300 text-left px-4 py-2">
          <h3 class="font-semibold">
            {{ $t('data.search.dates.placeholder') }}
          </h3>
          <p
            v-if="!localDateRange.start || !localDateRange.end"
            class="text-gray-400 my-1"
          >
            {{ $t('data.search.dates.label') }}
          </p>
          <p
            v-else
            class="flex my-1"
          >
            {{ $d(new Date(localDateRange.start), 'Y.numeric M.long D.numeric', locale) }}
            <YscIconsArrowRightLong class="h-6 w-6 mx-1" />
            {{ $d(new Date(localDateRange.end), 'Y.numeric M.long D.numeric', locale) }}
          </p>
        </div>
        <YscRangeCalendar
          ref="rangeCalendarRef"
          v-model="localDateRange"
          :min-date="minDate"
          :max-date="maxDate"
          class="mt-6 px-4"
        />
      </div>
    </form>
    <template #footer>
      <div class="flex items-center justify-between">
        <button
          v-if="!isLastStep"
          class="link text-gray-500 font-normal"
          @click="skipDestination"
        >
          {{ $t('commons.skip') }}
        </button>

        <template v-if="isLastStep">
          <button
            class="link text-gray-500"
            @click="localDateRange = onClearRange()"
          >
            <!-- TODO: fix -->
            {{ $t('commons.remove') }}
          </button>
          <YscButtonSubmit
            @click.prevent="onSubmit"
          >
            {{ $t('data.search.find') }}
          </YscButtonSubmit>
        </template>
      </div>
    </template>
  </YscModal>
</template>
